import buildConfigValidator from './utils/schema/build/buildConfigValidator';
import {SHADOW_ROOT_MODE_CLOSED, SHADOW_ROOT_MODE_OPEN} from './constants';
import {modifierTypes} from './effects';

const TYPE_ARRAY = {type: 'array'};
const TYPE_BOOLEAN = {type: 'boolean'};
const TYPE_FN = {fn: true};
const TYPE_NUMBER = {type: 'number'};
const TYPE_OBJECT = {type: 'object'};
const TYPE_STRING = {type: 'string'};

const properties = {
	...TYPE_OBJECT,
	additionalProperties: {
		...TYPE_OBJECT,
		properties: {
			default: {},
			computed: TYPE_FN,
			required: TYPE_BOOLEAN,
			selectable: TYPE_BOOLEAN,
			reflect: TYPE_BOOLEAN,
			deepCompare: TYPE_BOOLEAN,
			resolveRef: TYPE_BOOLEAN,
			schema: TYPE_OBJECT,
			unstableParse: {
				...TYPE_BOOLEAN,
				deprecated: 'has been deprecated, use Type Schemas instead'
			},
			unstablePreserveEmptyStr: {
				...TYPE_BOOLEAN,
				deprecated: 'has been deprecated, use Type Schemas instead'
			},
			dependencies: {
				...TYPE_ARRAY,
				items: TYPE_STRING
			},
			onChange: TYPE_FN
		},
		additionalProperties: false
	}
};

const interceptors = {
	...TYPE_ARRAY,
	items: {
		anyOf: [
			{
				...TYPE_OBJECT,
				properties: {
					before: TYPE_FN,
					after: TYPE_FN
				},
				additionalProperties: false
			},
			TYPE_FN
		]
	}
};

const actions = {
	...TYPE_OBJECT,
	additionalProperties: {
		...TYPE_OBJECT,
		properties: {
			handlers: {
				...TYPE_ARRAY,
				deprecated: 'has been deprecated, use actions handlers instead',
				items: {
					anyOf: [
						{
							...TYPE_OBJECT,
							properties: {
								effect: TYPE_FN,
								fn: TYPE_FN,
								args: TYPE_ARRAY
							},
							additionalProperties: false
						},
						TYPE_FN
					]
				}
			},
			interceptors: {
				...interceptors,
				deprecated: 'has been deprecated, use actions handler `interceptors` instead'
			},
			effect: {
				...TYPE_FN,
				deprecated: 'has been deprecated, use actions handlers instead'
			},
			private: {
				type: 'boolean',
				deprecated: 'has been deprecated, use actions handlers `stopPropagation` instead'
			},
			schema: TYPE_OBJECT
		},
		additionalProperties: false
	}
};

const actionHandlers = {
	...TYPE_OBJECT,
	additionalProperties: {
		anyOf: [
			{
				...TYPE_OBJECT,
				properties: {
					effect: TYPE_FN,
					args: TYPE_ARRAY,
					descendantOnly: TYPE_BOOLEAN,
					stopPropagation: TYPE_BOOLEAN,
					interceptors,
					modifier: {
						anyOf: [
							{
								...TYPE_OBJECT,
								properties: {
									name: {
										...TYPE_STRING,
										enum: [modifierTypes.DEBOUNCE]
									},
									delay: TYPE_NUMBER,
									maxWait: TYPE_NUMBER,
									collate: TYPE_BOOLEAN
								},
								additionalProperties: false
							},
							{
								...TYPE_OBJECT,
								properties: {
									name: {
										...TYPE_STRING,
										enum: [modifierTypes.THROTTLE]
									},
									limit: TYPE_NUMBER
								},
								additionalProperties: false
							},
							{
								...TYPE_OBJECT,
								properties: {
									name: {
										...TYPE_STRING,
										enum: [modifierTypes.TAKE_LATEST]
									}
								},
								additionalProperties: false
							}
						]
					}
				},
				additionalProperties: false
			},
			TYPE_FN
		]
	}
};

const eventHandlers = {
	...TYPE_ARRAY,
	items: {
		...TYPE_OBJECT,
		properties: {
			events: {
				...TYPE_ARRAY,
				minItems: 1,
				items: TYPE_STRING
			},
			effect: TYPE_FN,
			target: {
				anyOf: [TYPE_OBJECT, TYPE_STRING]
			},
			capture: TYPE_BOOLEAN,
			modifier: {
				anyOf: [
					{
						...TYPE_OBJECT,
						properties: {
							name: {
								...TYPE_STRING,
								enum: [modifierTypes.DEBOUNCE]
							},
							delay: TYPE_NUMBER,
							maxWait: TYPE_NUMBER,
							collate: TYPE_BOOLEAN
						},
						additionalProperties: false
					},
					{
						...TYPE_OBJECT,
						properties: {
							name: {
								...TYPE_STRING,
								enum: [modifierTypes.THROTTLE]
							},
							limit: TYPE_NUMBER
						},
						additionalProperties: false
					},
					{
						...TYPE_OBJECT,
						properties: {
							name: {
								...TYPE_STRING,
								enum: [modifierTypes.TAKE_LATEST]
							}
						},
						additionalProperties: false
					}
				]
			}
		},
		required: ['events', 'effect'],
		additionalProperties: false
	}
};

const initialState = {};

const setInitialState = {
	...TYPE_FN
};

const onBootstrap = {
	...TYPE_FN,
	deprecated: 'life cycle has been deprecated, use `COMPONENT_BOOTSTRAPPED` action handler instead'
};
const onConnect = {
	...TYPE_FN,
	deprecated: 'life cycle has been deprecated, use `COMPONENT_CONNECTED` action handler instead'
};

const onDisconnect = {
	...TYPE_FN,
	deprecated: 'life cycle has been deprecated, use `COMPONENT_DISCONNECTED` action handler instead'
};

const onPropertiesSet = {
	...TYPE_FN,
	deprecated: true
};

const behavior = {
	...TYPE_OBJECT,
	properties: {
		name: TYPE_STRING,
		properties,
		actions,
		actionHandlers,
		eventHandlers,
		initialState,
		setInitialState,
		onBootstrap,
		onConnect,
		onDisconnect,
		onPropertiesSet
	},
	additionalProperties: false
};

const presentationalBehavior = {
	...TYPE_OBJECT,
	properties: {
		name: TYPE_STRING,
		properties
	},
	additionalProperties: false
};

const validator = buildConfigValidator({
	...TYPE_OBJECT,
	properties: {
		behaviors: {
			...TYPE_ARRAY,
			items: {
				anyOf: [
					behavior,
					{
						...TYPE_OBJECT,
						properties: {
							behavior,
							options: TYPE_OBJECT
						},
						additionalProperties: false
					}
				]
			},
			uniqueItems: true
		},
		actions,
		actionHandlers,
		eventHandlers,
		initialState,
		setInitialState,
		properties,
		renderer: {
			...TYPE_OBJECT,
			properties: {
				view: TYPE_FN,
				transformState: TYPE_FN,
				transformProps: TYPE_FN,
				type: TYPE_OBJECT
			},
			additionalProperties: false
		},
		view: TYPE_FN,
		transformState: TYPE_FN,
		transformProps: TYPE_FN,
		type: TYPE_OBJECT,
		resources: {
			...TYPE_OBJECT,
			deprecated: 'has been deprecated, use effects instead'
		},
		actionTypes: {
			...TYPE_ARRAY,
			deprecated: true
		},
		actionTypesToProps: {
			...TYPE_OBJECT,
			deprecated: true
		},
		onBootstrap,
		onConnect,
		onDisconnect,
		onPropertiesSet,
		dispatches: TYPE_OBJECT,
		slots: TYPE_OBJECT,
		styles: TYPE_STRING,
		stylesheets: {
			...TYPE_ARRAY,
			items: TYPE_STRING
		},
		shadowRootOptions: {
			...TYPE_OBJECT,
			properties: {
				mode: {
					...TYPE_STRING,
					enum: [SHADOW_ROOT_MODE_OPEN, SHADOW_ROOT_MODE_CLOSED]
				}
			},
			additionalProperties: false
		}
	},
	additionalProperties: false
});

const presentationalValidator = buildConfigValidator({
	...TYPE_OBJECT,
	properties: {
		behaviors: {
			...TYPE_ARRAY,
			items: {
				anyOf: [
					presentationalBehavior,
					{
						...TYPE_OBJECT,
						properties: {
							behavior: presentationalBehavior,
							options: TYPE_OBJECT
						},
						additionalProperties: false
					}
				]
			},
			uniqueItems: true
		},
		properties,
		renderer: {
			...TYPE_OBJECT,
			properties: {
				type: TYPE_OBJECT
			},
			additionalProperties: false
		},
		view: TYPE_FN,
		transformProps: TYPE_FN,
		styles: TYPE_STRING,
		shadowRootOptions: {
			...TYPE_OBJECT,
			properties: {
				mode: {
					...TYPE_STRING,
					enum: [SHADOW_ROOT_MODE_OPEN, SHADOW_ROOT_MODE_CLOSED]
				}
			},
			additionalProperties: false
		}
	},
	additionalProperties: false
});

export function validatePresentationalConfig(name, config, options) {
	return presentationalValidator(config, `Config for ${name}: `, options);
}

export default function validateConfig(name, config, options) {
	return validator(config, `Config for ${name}: `, options);
}
